import {afterNextRender, Inject, Injectable, PLATFORM_ID} from '@angular/core';
import {isPlatformBrowser} from "@angular/common";
import {environment} from "../../../environments/environment";
import {AccessToken, LoginOptions} from "@capgo/capacitor-social-login";
import {Capacitor} from "@capacitor/core";
import {AbstractOauthService} from "./abstract-oauth-service";

export type GooglePkceLoginResponse = {
  responseType: "offline";
  serverAuthCode: string;
  codeVerifier?: string;
  redirectUri?: string;
  state?: string;
};

export type GoogleOnlineLoginResponse = {
  responseType: "online";
  accessToken: AccessToken | null;
  idToken: string | null;
  profile: {
    email: string | null;
    familyName: string | null;
    givenName: string | null;
    id: string | null;
    name: string | null;
    imageUrl: string | null;
  };
};

export type GoogleLoginResult = GoogleOnlineLoginResponse | GooglePkceLoginResponse;

@Injectable({
  providedIn: 'root'
})
export class GoogleService extends AbstractOauthService {
  private readonly oauthStateKey = 'OAUTH_STATE_KEY';
  private readonly oauthVerifierKey = 'OAUTH_GOOGLE_CODE_VERIFIER';
  private readonly oauthStateValueKey = 'OAUTH_GOOGLE_STATE';
  private readonly oauthRedirectKey = 'OAUTH_GOOGLE_REDIRECT_URI';
  private readonly oauthResultKey = 'OAUTH_GOOGLE_RESULT';
  googleLoginType = "online";
  constructor(
    @Inject(PLATFORM_ID) private platformId: object,
  ) {
    super();
    afterNextRender(async () => {
      if (!isPlatformBrowser(this.platformId)) {
        return;
      }
      const oauthStateKey = localStorage.getItem(this.oauthStateKey);
      if (oauthStateKey) {
        const params = new URL(window.location.href).searchParams;
        const oauthError = params.get('error');
        if (oauthError && params.get('state') === localStorage.getItem(this.oauthStateValueKey)) {
          const errorResult = {error: oauthError, state: params.get('state')};
          localStorage.setItem(this.oauthResultKey, JSON.stringify(errorResult));
          window.opener?.postMessage({type: 'oauth-error', ...errorResult}, window.location.origin);
          window.close();
          return;
        }
        const result = this.handleOAuthRedirect();
        if (result) {
          try {
            localStorage.setItem(this.oauthResultKey, JSON.stringify(result.result));
          } catch (e) {
            console.warn('Unable to persist OAuth result', e);
          }
          if (window.opener) {
            window.opener.postMessage(
              {type: "oauth-response", ...result.result},
              window.location.origin
            );
          }
          window.close();
        }
      }
    });
  }

  async init(): Promise<void> {
    await this.loadModule();

    let config: any = {}

    if (Capacitor.getPlatform() === 'ios') {
      config['iOSClientId'] = environment.googleIosClientId;
    } else {
      config['webClientId'] = environment.googleAndroidWebClientId;
    }
    // config['mode'] = 'offline';
    await this.module.SocialLogin.initialize({
      google: config,
    });
  }

  private async loadModule(): Promise<void> {
    if (!this.module?.SocialLogin) {
      this.module = await import('@capgo/capacitor-social-login');
    }
  }

  login(limited: boolean = false, includeCalendarScope: boolean = false): Promise<{ provider: 'google', result: GoogleLoginResult }> {
    const GOOGLE_SCOPES =  [
      "https://www.googleapis.com/auth/userinfo.email",
      "https://www.googleapis.com/auth/userinfo.profile",
      "openid",
      ...(includeCalendarScope ? ["https://www.googleapis.com/auth/calendar.events"] : []),
    ];

    const loginOptions: LoginOptions = {
      provider: 'google',
      options: {
        scopes: GOOGLE_SCOPES
      },
    };

    return (async () => {
      await this.logoutProvider('google');

      let res: { provider: 'google', result: GoogleLoginResult } | null = null;
      if (Capacitor.getPlatform() === 'web') {
        res = await this.fallbackToTraditionalOAuth(GOOGLE_SCOPES)
      } else {
        res = await this.module.SocialLogin.login(loginOptions);
      }

      return res;
    })();
  }

  async fallbackToTraditionalOAuth(scopes: string[]): Promise<{ provider: 'google', result: GoogleLoginResult }> {
    const uniqueScopes = [...new Set([...scopes, "openid"])];
    const redirectUri = `${window.location.origin}/login`;
    const codeVerifier = this.generateCodeVerifier(64);
    const codeChallenge = await this.createCodeChallenge(codeVerifier);
    const state = this.generateState();
    const params = new URLSearchParams({
      client_id: environment.googleAndroidWebClientId,
      redirect_uri: redirectUri,
      response_type: "code",
      scope: uniqueScopes.join(" "),
      code_challenge: codeChallenge,
      code_challenge_method: "S256",
      state: state,
      include_granted_scopes: "true",
    });
    const url = `https://accounts.google.com/o/oauth2/v2/auth?${params.toString()}`;
    const width = 500;
    const height = 600;
    const left = window.screenX + (window.outerWidth - width) / 2;
    const top = window.screenY + (window.outerHeight - height) / 2;

    try {
      localStorage.setItem(this.oauthStateKey, "true");
      localStorage.setItem(this.oauthVerifierKey, codeVerifier);
      localStorage.setItem(this.oauthStateValueKey, state);
      localStorage.setItem(this.oauthRedirectKey, redirectUri);
      const popup = window.open(url, "Google Sign In", `width=${width},height=${height},left=${left},top=${top},popup=1`);
      if (!popup) {
        throw new Error("Failed to open popup");
      }

      return new Promise((resolve, reject) => {
        let timeout: any;
        let storagePoll: any;
        let storageListener: ((event: StorageEvent) => void) | null = null;
        const cleanup = () => {
          clearTimeout(timeout);
          clearInterval(storagePoll);
          if (storageListener) {
            window.removeEventListener('storage', storageListener);
          }
          window.removeEventListener("message", handleMessage);
        };
        const clearOAuthState = () => {
          localStorage.removeItem(this.oauthStateKey);
          localStorage.removeItem(this.oauthVerifierKey);
          localStorage.removeItem(this.oauthStateValueKey);
          localStorage.removeItem(this.oauthRedirectKey);
          localStorage.removeItem(this.oauthResultKey);
        };
        const authorizationError = (error: string) => new Error(
          error === 'access_denied' ? 'Google authorization cancelled' : 'Google authorization failed'
        );
        const handleMessage = (event: MessageEvent) => {
          if (event.origin !== window.location.origin) {
            return;
          }
          if (!event.data || !['oauth-response', 'oauth-error'].includes(event.data.type)) {
            return;
          }
          if (event.data.state && event.data.state !== state) {
            return;
          }
          cleanup();
          clearOAuthState();
          if (event.data.type === 'oauth-error') {
            reject(authorizationError(event.data.error));
            popup.close();
            return;
          }
          resolve({
            provider: "google",
            result: {
              responseType: "offline",
              serverAuthCode: event.data.serverAuthCode || event.data.code,
              codeVerifier: event.data.codeVerifier,
              redirectUri: event.data.redirectUri,
              state: event.data.state,
            },
          });
          popup.close();
        };

        window.addEventListener("message", handleMessage);

        storageListener = (event: StorageEvent) => {
          if (event.key !== this.oauthResultKey || !event.newValue) {
            return;
          }
          try {
            const parsed = JSON.parse(event.newValue);
            if (parsed.state && parsed.state !== state) {
              return;
            }
            cleanup();
            clearOAuthState();
            if (parsed.error) {
              reject(authorizationError(parsed.error));
              popup.close();
              return;
            }
            resolve({
              provider: "google",
              result: parsed,
            });
            popup.close();
          } catch (e) {
            console.warn('Invalid stored OAuth result', e);
          }
        };

        window.addEventListener('storage', storageListener);

        storagePoll = setInterval(() => {
          const stored = localStorage.getItem(this.oauthResultKey);
          if (!stored) {
            if (popup.closed) {
              cleanup();
              clearOAuthState();
              reject(new Error('Google authorization cancelled'));
            }
            return;
          }
          try {
            const parsed = JSON.parse(stored);
            if (parsed.state && parsed.state !== state) {
              return;
            }
            cleanup();
            clearOAuthState();
            if (parsed.error) {
              reject(authorizationError(parsed.error));
              popup.close();
              return;
            }
            resolve({
              provider: "google",
              result: parsed,
            });
            popup.close();
          } catch (e) {
            console.warn('Invalid stored OAuth result', e);
          }
        }, 500);

        timeout = setTimeout(() => {
          cleanup();
          clearOAuthState();
          popup.close();
          reject(new Error("OAuth timeout after 5 minutes"));
        }, 300000);
      });
    } catch (err) {
      localStorage.removeItem(this.oauthStateKey);
      localStorage.removeItem(this.oauthVerifierKey);
      localStorage.removeItem(this.oauthStateValueKey);
      localStorage.removeItem(this.oauthRedirectKey);
      throw err;
    }
  }

  persistStateGoogle(accessToken: string, idToken: string): void {
    try {
      window.localStorage.setItem("capgo_social_login_google_state", JSON.stringify({ accessToken, idToken }));
    }
    catch (e) {
      console.error("Cannot persist state google", e);
    }
  }

  parseJwt(token: string) {
    const base64Url = token.split(".")[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(atob(base64)
      .split("")
      .map((c) => {
        return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
      })
      .join(""));
    return JSON.parse(jsonPayload);
  }

  handleOAuthRedirect(): { provider: 'google', result: GoogleLoginResult } | null {
    const paramsRaw = new URL(window.location.href).searchParams;
    const code = paramsRaw.get("code");
    const state = paramsRaw.get("state");
    const expectedState = localStorage.getItem(this.oauthStateValueKey);
    if (code && state && expectedState && state === expectedState) {
      const codeVerifier = localStorage.getItem(this.oauthVerifierKey);
      const redirectUri = localStorage.getItem(this.oauthRedirectKey);
      return {
        provider: "google",
        result: {
          responseType: "offline",
          serverAuthCode: code,
          codeVerifier: codeVerifier,
          redirectUri: redirectUri,
          state: state,
        },
      };
    }
    const hash = window.location.hash.substring(1);
    if (!hash)
      return;
    const params = new URLSearchParams(hash);
    const accessToken = params.get("access_token");
    const idToken = params.get("id_token");
    if (accessToken && idToken) {
      localStorage.removeItem('OAUTH_STATE_KEY');
      const profile = this.parseJwt(idToken);
      return {
        provider: "google",
        result: {
          responseType: "online",
          accessToken: {
            token: accessToken,
          },
          idToken,
          profile: {
            email: profile.email || null,
            familyName: profile.family_name || null,
            givenName: profile.given_name || null,
            id: profile.sub || null,
            name: profile.name || null,
            imageUrl: profile.picture || null,
          },
        },
      };
    }
    return null;
  }

  private generateCodeVerifier(length: number): string {
    const charset = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~';
    const randomValues = new Uint8Array(length);
    crypto.getRandomValues(randomValues);
    let result = '';
    for (let i = 0; i < randomValues.length; i++) {
      result += charset[randomValues[i] % charset.length];
    }
    return result;
  }

  private generateState(): string {
    return this.generateCodeVerifier(32);
  }

  private async createCodeChallenge(verifier: string): Promise<string> {
    const data = new TextEncoder().encode(verifier);
    const digest = await crypto.subtle.digest('SHA-256', data);
    return this.base64UrlEncode(new Uint8Array(digest));
  }

  private base64UrlEncode(buffer: Uint8Array): string {
    let binary = '';
    buffer.forEach((b) => {
      binary += String.fromCharCode(b);
    });
    return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  }
}
